import {
  STATS_REPORT_TYPE_MERGED,
  getDefaultStatsReportType,
  isMultiOrgStatsAdmin,
  isNonInputAnalystUser,
  isStatsReportTypeAllowed,
  isSystemStatsAdmin,
  resolveStatsReportTypeForRequest,
} from './statistics-report-type'

/** basicOrganization su ChartRequest / GET crash/all (1 Police, 2 Hospital, 3 Insurance). */
export const STATS_BASIC_ORGANIZATION = Object.freeze({
  POLICE: 1,
  HOSPITAL: 2,
  INSURANCE: 3,
})

const STATS_BASIC_ORGANIZATION_IDS = [
  STATS_BASIC_ORGANIZATION.POLICE,
  STATS_BASIC_ORGANIZATION.HOSPITAL,
  STATS_BASIC_ORGANIZATION.INSURANCE,
]

/** Valore select crash list: dataset merged (GET /crash/all?merged=1). */
export const CRASH_LIST_DATASET_FILTER_MERGED = 'merged'

/**
 * Analyst ministeriale: Crash List solo su dataset merged nazionale.
 * @param {object} [user]
 */
export function shouldLockCrashListDatasetToMerged(user) {
  return isNonInputAnalystUser(user)
}

/**
 * Filtro dataset iniziale per Crash List.
 * @param {object} [user]
 */
export function getDefaultCrashListDatasetFilter(user) {
  return shouldLockCrashListDatasetToMerged(user) ? CRASH_LIST_DATASET_FILTER_MERGED : ''
}

/**
 * Opzioni UI categoria dati (basicOrganization). Passare label già tradotte.
 * @param {{ allCategories: string, police: string, hospital: string, insurance: string, merged?: string }} labels
 * @param {{ includeMerged?: boolean }} [options]
 */
export function getStatsBasicOrganizationOptions(labels, options = {}) {
  const items = [
    { id: '', label: labels.allCategories },
  ]
  if (options.includeMerged && labels.merged) {
    items.push({ id: CRASH_LIST_DATASET_FILTER_MERGED, label: labels.merged })
  }
  items.push(
    { id: STATS_BASIC_ORGANIZATION.POLICE, label: labels.police },
    { id: STATS_BASIC_ORGANIZATION.HOSPITAL, label: labels.hospital },
    { id: STATS_BASIC_ORGANIZATION.INSURANCE, label: labels.insurance },
  )
  return items
}

/**
 * @param {object} [org]
 * @returns {number | null}
 */
function resolveOrganizationBasicId(org) {
  const basic = Number(org?.basic_id ?? org?.basic ?? org?.basicOrganization)
  return STATS_BASIC_ORGANIZATION_IDS.includes(basic) ? basic : null
}

/**
 * Categorie basicOrganization (1 Police, 2 Hospital, 3 Insurance) visibili all'utente.
 * Allineato a scope crash list / Report Type (`getAccessibleStatsReportTypes`).
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 * @returns {number[]}
 */
export function getAccessibleBasicOrganizationIds(user, organizations = []) {
  if (!user) return [...STATS_BASIC_ORGANIZATION_IDS]

  if (isSystemStatsAdmin(user) || isNonInputAnalystUser(user)) {
    return [...STATS_BASIC_ORGANIZATION_IDS]
  }

  const orgIdsToResolve = new Set()
  const userOrgId = Number(user.organization)
  if (Number.isFinite(userOrgId) && userOrgId > 0) {
    orgIdsToResolve.add(userOrgId)
  }

  const userType = Number(user.type)
  if (isMultiOrgStatsAdmin(user) || userType === 2) {
    const managedIds = Array.isArray(user.organization_ids)
      ? user.organization_ids.map(Number).filter((id) => id > 0)
      : []
    managedIds.forEach((id) => orgIdsToResolve.add(id))
  }

  const allOrgs = organizations.filter((o) => Number(o.id ?? o.value) !== -1)
  const basicIds = [
    ...new Set(
      allOrgs
        .filter((o) => orgIdsToResolve.has(Number(o.id ?? o.value)))
        .map(resolveOrganizationBasicId)
        .filter((id) => id != null),
    ),
  ]

  if (basicIds.length > 0) return basicIds

  const userBasic = Number(user.basic_organization)
  if (STATS_BASIC_ORGANIZATION_IDS.includes(userBasic)) {
    return [userBasic]
  }

  return []
}

/**
 * Filtro "Merged" nella crash list: analyst / admin con scope aggregato.
 * @param {object} [user]
 */
export function canUseMergedDatasetFilter(user) {
  if (!user) return false
  if (isSystemStatsAdmin(user) || isNonInputAnalystUser(user) || isMultiOrgStatsAdmin(user)) {
    return true
  }
  // Organization admin (type 2, org operativa)
  if (Number(user.type) === 2 && !isNonInputAnalystUser(user)) {
    return true
  }
  return false
}

/**
 * Opzioni categoria dati filtrate per utente (crash list, dashboard merged, custom chart).
 * @param {{ allCategories: string, police: string, hospital: string, insurance: string, merged?: string }} labels
 * @param {{ user?: object, organizations?: Array<object>, includeMerged?: boolean }} [options]
 */
export function getAccessibleStatsBasicOrganizationOptions(labels, options = {}) {
  const { user, organizations = [], includeMerged = false } = options

  if (isNonInputAnalystUser(user)) {
    if (!includeMerged || !labels.merged) return []
    return [{ id: CRASH_LIST_DATASET_FILTER_MERGED, label: labels.merged }]
  }

  const allowedBasicIds = getAccessibleBasicOrganizationIds(user, organizations)
  const showMerged = includeMerged && canUseMergedDatasetFilter(user)
  const allOptions = getStatsBasicOrganizationOptions(labels, { includeMerged: showMerged })

  const canSeeAllCategories =
    isSystemStatsAdmin(user) ||
    allowedBasicIds.length > 1

  return allOptions.filter((opt) => {
    if (opt.id === '') {
      return canSeeAllCategories || allowedBasicIds.length > 0
    }
    if (opt.id === CRASH_LIST_DATASET_FILTER_MERGED) {
      return showMerged
    }
    return allowedBasicIds.includes(Number(opt.id))
  })
}

/**
 * @param {number | string | null | undefined} value
 * @returns {number | null}
 */
export function parseStatsBasicOrganization(value) {
  if (value === '' || value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

/**
 * basicOrganization è valido solo con reportType Merged (0) o legacy ministero (backend).
 * @param {number} reportType
 */
export function canUseBasicOrganizationWithReportType(reportType) {
  return Number(reportType) <= STATS_REPORT_TYPE_MERGED
}

/**
 * Parametri top-level merged / basicOrganization (parità GET /crash/all).
 * @param {{ reportType: number, basicOrganization?: number | string | null }} params
 * @returns {{ merged?: number, basicOrganization?: number }}
 */
export function buildChartRequestDatasetExtras({ reportType, basicOrganization }) {
  const rt = Number(reportType)
  /** @type {{ merged?: number, basicOrganization?: number }} */
  const extras = {}

  if (rt === STATS_REPORT_TYPE_MERGED) {
    extras.merged = 1
  }

  const basic = parseStatsBasicOrganization(basicOrganization)
  if (basic != null && canUseBasicOrganizationWithReportType(rt)) {
    extras.basicOrganization = basic
  }

  return extras
}

/**
 * Query string params per GET /crash/all (parità Stats).
 * Filtro "Merged" → `merged=1`; categoria org → `basicOrganization=1|2|3` (mutuamente esclusivi).
 * @param {{ merged?: boolean | null, basicOrganization?: number | string | null, datasetFilter?: string | number | null, user?: object }} params
 * @returns {Record<string, string | number>}
 */
export function buildCrashListDatasetQueryParams({
  merged,
  basicOrganization,
  datasetFilter,
  user,
}) {
  /** @type {Record<string, string | number>} */
  const params = {}

  if (shouldLockCrashListDatasetToMerged(user)) {
    params.merged = 1
    return params
  }

  const filter = datasetFilter ?? basicOrganization ?? ''

  if (filter === CRASH_LIST_DATASET_FILTER_MERGED) {
    params.merged = 1
    return params
  }

  if (merged === true) params.merged = 1
  else if (merged === false) params.merged = 0

  const basic = parseStatsBasicOrganization(filter)
  if (basic != null) params.basicOrganization = basic

  return params
}

/**
 * Arricchisce payload POST /Stats con reportType risolto e merged/basicOrganization.
 * @param {object} payload
 * @param {object} context
 * @param {object} [context.user]
 * @param {Array<object>} [context.organizations]
 * @param {number} [context.reportType]
 * @param {number | string | null} [context.basicOrganization]
 */
export function enrichStatsRequestPayload(
  payload,
  { user, organizations = [], reportType, basicOrganization },
) {
  const resolvedReportType = resolveStatsReportTypeForRequest(
    user,
    reportType ?? payload?.chartField?.reportType,
    organizations,
  )

  if (
    !isStatsReportTypeAllowed(resolvedReportType, user, organizations) &&
    user
  ) {
    const fallback = getDefaultStatsReportType(user)
    return enrichStatsRequestPayload(payload, {
      user,
      organizations,
      reportType: fallback,
      basicOrganization: canUseBasicOrganizationWithReportType(fallback)
        ? basicOrganization
        : null,
    })
  }

  const chartField = payload?.chartField
    ? { ...payload.chartField, reportType: resolvedReportType }
    : { reportType: resolvedReportType }

  const datasetExtras = buildChartRequestDatasetExtras({
    reportType: resolvedReportType,
    basicOrganization: canUseBasicOrganizationWithReportType(resolvedReportType)
      ? basicOrganization
      : null,
  })

  return {
    ...payload,
    ...datasetExtras,
    chartField,
  }
}
